"use struct";

import $ from "jquery";

(function () {
  const NETWORKID = '3948';

  /**
   * Wait for CMP to be ready (TCF or GPP)
   * @returns {Promise<boolean>} Promise that resolves to true when CMP is ready, false if no CMP found
   */
  /**
   * Check if prebid.js should be loaded based on GDPR consent
   * @returns {boolean} True if prebid should be loaded, false otherwise
   */
  const checkPrebidConsent = () => {
    // Check GPP first (US and other regions without GDPR dialog)
    if (typeof window.__gpp === 'function') {
      let gppResult = true;
      window.__gpp('ping', (pingData) => {
        if (pingData && pingData.cmpStatus === 'loaded' && pingData.signalStatus === 'ready') {
          // Check if TCF is also present (GDPR dialog shown)
          if (typeof window.__tcfapi === 'function') {
            // Both GPP and TCF present - defer to TCF consent check
            gppResult = checkTCFConsentSync();
          } else {
            // GPP only - allow prebid
            gppResult = true;
          }
        }
      });
      return gppResult;
    }

    // Check TCF for GDPR regions or regions with GDPR dialog
    if (typeof window.__tcfapi === 'function') {
      return checkTCFConsentSync();
    }

    // No CMP found - deny prebid by default
    return false;
  };

  /**
   * Synchronously check TCF consent for prebid loading
   * @returns {boolean} True if consent is granted for Purpose 3 or 4
   */
  const checkTCFConsentSync = () => {
    let consentGranted = false;
    window.__tcfapi('getTCData', 2, function(tcData, success) {
      if (success && tcData) {
        // Check consent for Purpose 3 (ad profiles) or 4 (ad selection)
        consentGranted = tcData.purpose && tcData.purpose.consents &&
                        (tcData.purpose.consents[3] || tcData.purpose.consents[4]);
      }
    });
    return consentGranted;
  };

  const waitForCMPReady = () => {
    return new Promise((resolve) => {
      // Check GPP first
      if (typeof window.__gpp === 'function') {
        window.__gpp('ping', (pingData) => {
          if (pingData && (pingData.cmpStatus === 'loaded' || pingData.signalStatus === 'ready')) {
            resolve(true);
          } else {
            // Wait for GPP to be ready
            const checkGPP = () => {
              window.__gpp('ping', (data) => {
                if (data && (data.cmpStatus === 'loaded' || data.signalStatus === 'ready')) {
                  resolve(true);
                } else {
                  setTimeout(checkGPP, 100);
                }
              });
            };
            checkGPP();
          }
        });
        return;
      }

      // Check TCF for GDPR regions
      if (typeof window.__tcfapi === 'function') {
        window.__tcfapi('addEventListener', 2, function(tcData, success) {
          if (success && tcData && (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete')) {
            resolve(true);
          }
        });
        return;
      }

      // No CMP found - do not load ads
      resolve(false);
    });
  };

  const link = (siteId) => {
    return new Promise((resolve) => {
      if (siteId === null) {
        resolve(false);
        return;
      }

      // Wait for CMP to be ready, then load SAS scripts
      waitForCMPReady().then((cmpFound) => {
        if (!cmpFound) {
            resolve(false);
          return;
        }

        // Load smart.js and wait for it to complete
        const smartScript = document.createElement('script');
        smartScript.src = 'https://ced.sascdn.com/tag/' + NETWORKID + '/smart.js';
        smartScript.async = true;
        smartScript.type = 'application/javascript';

        smartScript.addEventListener('load', () => {
          // Check if prebid should be loaded based on consent
          const shouldLoadPrebid = checkPrebidConsent();

          if (shouldLoadPrebid) {
            // Load prebid.js after smart.js is loaded
            const prebidScript = document.createElement('script');
            prebidScript.src = 'https://tagmanager.smartadserver.com/' + NETWORKID + '/' + siteId + '/smart.prebid.js';
            prebidScript.async = true;
            prebidScript.type = 'text/javascript';

            prebidScript.addEventListener('load', () => {
              resolve(true);
            });

            prebidScript.addEventListener('error', () => {
              console.log('Prebid script failed to load, continuing with smart.js only');
              resolve(true);
            });

            document.head.appendChild(prebidScript);
          } else {
            resolve(true);
          }
        });

        smartScript.addEventListener('error', () => {
          console.log('Smart.js failed to load');
          resolve(false);
        });

        document.head.appendChild(smartScript);
      });
    });
  };

  const init = (siteId, pageId, formats, target) => {
    if (siteId === null || pageId === null || !formats.length) return false;

    const SAS = window.sas || {};

    var recall_flg = true;

    SAS.cmd = SAS.cmd || [];
    SAS.cmd.push(function () {
      SAS.setup({ networkid: NETWORKID, domain: "https://www18.smartadserver.com", async: true, renderMode: 2 });
    });

    let tagIds = [];
    formats.forEach((format) => {
      tagIds.push(format.tagId);
    });

    // target で 1つの Key に複数 Value を設定するための処理
    SAS.setTargeting({
      target: target,
      mode: 1,
      tagIds: tagIds
    })

    SAS.cmd.push(function () {
      SAS.call("onecall", {
        siteId: siteId,
        pageId: pageId,
        formats: formats,
      }, {
        onNoad: function(data){
          if (recall_flg) {
            SAS.refresh(data.formatId);
            recall_flg = false;
          } else {
            var element = document.getElementById(data.tagId);
            if (element) {
              element.style.height = 'auto';
              element.style.width = 'auto';
            }
          }
        },
        onAd: function(data){
          var element = document.getElementById(data.tagId);
          if (element) {
            element.style.height = 'auto';
            element.style.width = 'auto';
          }
        }
      });
    });

    for (let index of Object.keys(formats)) {
      const FORMAT = formats[index];

      SAS.cmd.push(function () {
        SAS.render(FORMAT.tagId);
      });

      if (FORMAT.anchor) {
        const ANCHOR = window.MAL.ad.anchor;
        ANCHOR.shouldSilence() ? '' : ANCHOR.prepare(null, true);
      };
    };
  };

  const getTarget = ($element) => {
    let decodedTargets     = decodeURIComponent($element.attr('data-ad-target'));
    let jsonDecodedTargets = JSON.parse(decodedTargets);
    return jsonDecodedTargets;
  }

  const getInfo = () => {
    let siteId = null;
    let pageId = null;
    let formats = [];
    let target = {};

    $('.ad-sas').each((index, element) => {
      formats[index] = {
        id: $(element).attr('data-ad-id'),
        tagId: $(element).attr('id'),
        anchor: $(element).is('[data-anchor]')
      };

      if (index === 0) {
        pageId = $(element).attr('data-ad-pid');
        siteId = $(element).attr('data-ad-sid');
        target = getTarget($(element));
      };
    });

    return {
      siteId: siteId,
      pageId: pageId,
      formats: formats,
      target: target,
    }
  };

  const execute = () => {
    let info = {};
    const promise = new Promise((resolve) => {
      document.addEventListener('DOMContentLoaded', () => {
        info = getInfo();
        resolve();
      }, false);
    });

    promise.then(() => {
      link(info.siteId).then((scriptsLoaded) => {
        if (scriptsLoaded) {
          // Wait for window load event before initializing
          if (document.readyState === 'complete') {
            init(info.siteId, info.pageId, info.formats, info.target);
          } else {
            window.addEventListener('load', () => {
              init(info.siteId, info.pageId, info.formats, info.target);
            }, false);
          }
        }
      });
    });
  };

  execute();
})();
